import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  FileText, 
  DollarSign, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Copy,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Clock,
  RefreshCw,
  LogOut,
  Package,
  Truck,
  PackagePlus,
  Ticket,
  BookOpen,
  Link as LinkIcon
} from 'lucide-react';
import { toast } from "sonner";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CJDropshippingPanel from './CJDropshippingPanel';
import CustomProductsPanel from './CustomProductsPanel';
import SupplierOrdersPanel from './SupplierOrdersPanel';
import DiscountCodesPanel from './DiscountCodesPanel';
import GuidesPanel from './GuidesPanel';
import DirectLinksPanel from './DirectLinksPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [activeTab, setActiveTab] = useState('create'); // 'create' | 'invoices' | 'payments' | 'orders'
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [toolsCatalog, setToolsCatalog] = useState([]);

  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    client_nif: '',
    service_name: '',
    service_description: '',
    amount: '',
    currency: 'USD',
    tax_rate: '0.08',
    notes: '',
    send_email: true,
    mark_as_paid: false
  });

  // Fetch payments when tab changes to 'payments'
  useEffect(() => {
    if (activeTab === 'payments') {
      fetchPayments();
    } else if (activeTab === 'invoices') {
      fetchInvoices();
    } else if (activeTab === 'orders') {
      fetchOrders();
    } else if (activeTab === 'cj') {
      // Load tools catalog once when entering CJ tab
      if (toolsCatalog.length === 0) {
        axios.get(`${API}/tools/`).then((r) => {
          const data = Array.isArray(r.data) ? r.data : (r.data?.tools || []);
          setToolsCatalog(data);
        }).catch(() => {});
      }
    }
  }, [activeTab]);

  const fetchPayments = async () => {
    try {
      setLoadingPayments(true);
      const response = await axios.get(`${API}/payments/transactions?limit=50`);
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Error al cargar los pagos');
    } finally {
      setLoadingPayments(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoadingInvoices(true);
      const response = await axios.get(`${API}/invoices/?limit=100`);
      setInvoices(response.data?.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Error al cargar las facturas');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      const response = await axios.get(`${API}/orders/?limit=200`);
      setOrders(response.data?.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Error al cargar los pedidos');
    } finally {
      setLoadingOrders(false);
    }
  };

  const updateOrder = async (orderId, payload) => {
    try {
      setUpdatingOrderId(orderId);
      await axios.patch(`${API}/orders/${orderId}`, payload);
      toast.success('Pedido actualizado');
      await fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(error?.response?.data?.detail || 'No se pudo actualizar el pedido');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const markOrderPaid = async (orderId) => {
    if (!window.confirm('¿Marcar este pedido como PAGADO y enviar el email de confirmación al cliente?')) {
      return;
    }
    try {
      setUpdatingOrderId(orderId);
      const res = await axios.post(`${API}/orders/${orderId}/mark-paid`);
      if (res.data?.email_sent) {
        toast.success('Pedido marcado como pagado. Email enviado al cliente.');
      } else {
        toast.warning('Pedido marcado como pagado, pero hubo un problema enviando el email.');
      }
      await fetchOrders();
    } catch (error) {
      console.error('Error marking order paid:', error);
      toast.error(error?.response?.data?.detail || 'No se pudo marcar como pagado');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const resendOrderEmail = async (orderId) => {
    if (!window.confirm('¿Reenviar el email de confirmación + factura al cliente?')) {
      return;
    }
    try {
      setUpdatingOrderId(orderId);
      const res = await axios.post(`${API}/orders/${orderId}/resend-email`);
      if (res.data?.email_sent) {
        const inv = res.data?.invoice_number;
        toast.success(inv
          ? `Email enviado con factura ${inv}`
          : 'Email reenviado al cliente.');
      } else {
        toast.warning('Hubo un problema enviando el email.');
      }
      await fetchOrders();
    } catch (error) {
      console.error('Error resending order email:', error);
      toast.error(error?.response?.data?.detail || 'No se pudo reenviar el email');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleMarkInvoicePaid = async (invoiceNumber) => {
    const confirmed = window.confirm(
      `¿Marcar la factura ${invoiceNumber} como PAGADA?\n\nSe enviará un recibo de pago al cliente por email.`
    );
    if (!confirmed) return;

    try {
      setMarkingPaidId(invoiceNumber);
      const response = await axios.patch(
        `${API}/invoices/${encodeURIComponent(invoiceNumber)}/mark-paid`,
        { send_email: true }
      );
      const data = response.data || {};
      if (data.already_paid) {
        toast.info('Esta factura ya estaba marcada como pagada.');
      } else {
        toast.success(
          data.email_sent
            ? `Factura ${invoiceNumber} marcada como pagada. Recibo enviado al cliente.`
            : `Factura ${invoiceNumber} marcada como pagada.`
        );
      }
      // Refresh list
      await fetchInvoices();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      const msg = error?.response?.data?.detail || 'No se pudo marcar la factura como pagada';
      toast.error(msg);
    } finally {
      setMarkingPaidId(null);
    }
  };

  const generateInvoiceFromPayment = (payment) => {
    // Pre-fill form with payment data
    setFormData({
      client_name: payment.customer_name || '',
      client_email: payment.customer_email || '',
      client_phone: '',
      client_address: '',
      client_nif: '',
      service_name: payment.package_name || 'Servicio de Drywall',
      service_description: `Pago por ${payment.package_name || 'servicio'}`,
      amount: payment.amount?.toString() || '',
      currency: payment.currency?.toUpperCase() || 'USD',
      tax_rate: '0',
      notes: `ID Transacción Stripe: ${payment.session_id || 'N/A'}`,
      send_email: true,
      mark_as_paid: true
    });
    setActiveTab('create');
    toast.success('Datos del pago cargados. Completa los campos y genera la factura.');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación
    if (!formData.client_name || !formData.client_email || !formData.amount || !formData.service_name) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/invoices/create-payment-link`, {
        ...formData,
        amount: parseFloat(formData.amount),
        tax_rate: parseFloat(formData.tax_rate)
      });

      setGeneratedInvoice(response.data);
      
      toast.success("¡Factura creada exitosamente!", {
        description: "El enlace está listo para enviar al cliente",
        duration: 5000,
      });

    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al crear la factura", {
        description: error.response?.data?.detail || "Por favor intenta de nuevo",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("¡Copiado!", {
      description: "Enlace copiado al portapapeles",
      duration: 2000,
    });
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      client_email: '',
      client_phone: '',
      client_address: '',
      client_nif: '',
      service_name: '',
      service_description: '',
      amount: '',
      currency: 'USD',
      tax_rate: '0.08',
      notes: '',
      send_email: true,
      mark_as_paid: false
    });
    setGeneratedInvoice(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
          <div className="text-center sm:text-left flex-1">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Panel de Administración
            </h1>
            <p className="text-slate-600">
              Genera facturas con enlaces de pago para tus clientes
            </p>
          </div>
          <div className="flex items-center gap-3" data-testid="admin-user-bar">
            <div className="text-right">
              <p className="text-xs text-slate-500">Sesión iniciada como</p>
              <p className="text-sm font-medium text-slate-700" data-testid="admin-user-email">
                {user?.email}
              </p>
            </div>
            <Button
              data-testid="admin-logout-btn"
              onClick={async () => {
                await logout();
                navigate('/admin/login', { replace: true });
              }}
              variant="outline"
              className="border-slate-300 hover:border-red-600 hover:text-red-600"
            >
              <LogOut size={16} className="mr-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            data-testid="admin-tab-create"
            onClick={() => setActiveTab('create')}
            className={`flex-1 min-w-[180px] py-3 ${activeTab === 'create' 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
          >
            <FileText size={18} className="mr-2" />
            Crear Factura
          </Button>
          <Button
            data-testid="admin-tab-invoices"
            onClick={() => setActiveTab('invoices')}
            className={`flex-1 min-w-[180px] py-3 ${activeTab === 'invoices' 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
          >
            <FileText size={18} className="mr-2" />
            Facturas
          </Button>
          <Button
            data-testid="admin-tab-payments"
            onClick={() => setActiveTab('payments')}
            className={`flex-1 min-w-[180px] py-3 ${activeTab === 'payments' 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
          >
            <CreditCard size={18} className="mr-2" />
            Pagos Stripe
          </Button>
          <Button
            data-testid="admin-tab-orders"
            onClick={() => setActiveTab('orders')}
            className={`flex-1 min-w-[180px] py-3 ${activeTab === 'orders' 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
          >
            <Package size={18} className="mr-2" />
            Pedidos Tienda
          </Button>
          <Button
            data-testid="admin-tab-cj"
            onClick={() => setActiveTab('cj')}
            className={`flex-1 min-w-[180px] py-3 ${activeTab === 'cj' 
              ? 'bg-orange-500 hover:bg-orange-600 text-white' 
              : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
          >
            <Truck size={18} className="mr-2" />
            CJ Dropshipping
          </Button>
          <Button
            data-testid="admin-tab-custom"
            onClick={() => setActiveTab('custom')}
            className={`flex-1 min-w-[180px] py-3 ${activeTab === 'custom' 
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
              : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
          >
            <PackagePlus size={18} className="mr-2" />
            Productos Manuales
          </Button>
          <Button
            data-testid="admin-tab-supplier"
            onClick={() => setActiveTab('supplier')}
            className={`flex-1 min-w-[180px] py-3 ${activeTab === 'supplier' 
              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
              : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
          >
            <Truck size={18} className="mr-2" />
            Pedidos Proveedor
          </Button>
          <Button
            data-testid="admin-tab-discounts"
            onClick={() => setActiveTab('discounts')}
            className={`flex-1 min-w-[180px] py-3 ${activeTab === 'discounts' 
              ? 'bg-pink-600 hover:bg-pink-700 text-white' 
              : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
          >
            <Ticket size={18} className="mr-2" />
            Descuentos
          </Button>
          <Button
            data-testid="admin-tab-guides"
            onClick={() => setActiveTab('guides')}
            className={`flex-1 min-w-[180px] py-3 ${activeTab === 'guides' 
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
              : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
          >
            <BookOpen size={18} className="mr-2" />
            Guías
          </Button>
          <Button
            data-testid="admin-tab-links"
            onClick={() => setActiveTab('links')}
            className={`flex-1 min-w-[180px] py-3 ${activeTab === 'links' 
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
              : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
          >
            <LinkIcon size={18} className="mr-2" />
            Enlaces bio
          </Button>
        </div>

        {activeTab === 'cj' && (
          <CJDropshippingPanel tools={toolsCatalog} />
        )}

        {activeTab === 'custom' && (
          <CustomProductsPanel />
        )}

        {activeTab === 'supplier' && (
          <SupplierOrdersPanel />
        )}

        {activeTab === 'discounts' && (
          <DiscountCodesPanel />
        )}

        {activeTab === 'guides' && (
          <GuidesPanel />
        )}

        {activeTab === 'links' && (
          <DirectLinksPanel />
        )}

        {/* Invoices List Tab */}
        {activeTab === 'invoices' && (
          <Card className="border-0 shadow-2xl mb-6" data-testid="invoices-list-card">
            <CardHeader className="bg-gradient-to-r from-slate-900 to-red-600 text-white">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-3">
                  <FileText size={24} />
                  Historial de Facturas
                </CardTitle>
                <Button
                  data-testid="invoices-refresh-btn"
                  onClick={fetchInvoices}
                  disabled={loadingInvoices}
                  className="bg-white/20 hover:bg-white/30 text-white"
                  size="sm"
                >
                  <RefreshCw size={16} className={`mr-2 ${loadingInvoices ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {loadingInvoices ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent mx-auto mb-4"></div>
                  <p className="text-slate-600">Cargando facturas...</p>
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8">
                  <FileText size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No hay facturas generadas todavía</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {invoices.map((invoice) => {
                    const isPaid = invoice.status === 'paid';
                    const isBusy = markingPaidId === invoice.invoice_number;
                    const currencySym = (invoice.currency || 'USD').toUpperCase() === 'EUR' ? '€' : '$';
                    return (
                      <div
                        key={invoice.invoice_number}
                        data-testid={`invoice-row-${invoice.invoice_number}`}
                        className={`border rounded-lg p-4 ${
                          isPaid ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50/40'
                        }`}
                      >
                        <div className="flex flex-wrap justify-between items-start gap-3">
                          <div className="flex-1 min-w-[220px]">
                            <div className="flex items-center gap-2 mb-2">
                              {isPaid ? (
                                <CheckCircle size={18} className="text-green-600" />
                              ) : (
                                <Clock size={18} className="text-yellow-600" />
                              )}
                              <span className={`font-semibold text-xs uppercase tracking-wide ${
                                isPaid ? 'text-green-800' : 'text-yellow-800'
                              }`}>
                                {isPaid ? 'PAGADA' : 'PENDIENTE'}
                              </span>
                              <span className="font-mono text-xs text-slate-500">
                                · {invoice.invoice_number}
                              </span>
                            </div>
                            <p className="font-medium text-slate-900">
                              {invoice.client_name || 'Cliente sin nombre'}
                            </p>
                            <p className="text-sm text-slate-600">
                              {invoice.client_email || 'Email no disponible'}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Creada: {formatDate(invoice.created_at)}
                              {isPaid && invoice.paid_at && ` · Pagada: ${formatDate(invoice.paid_at)}`}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-slate-900">
                              {currencySym}{Number(invoice.amount || 0).toFixed(2)}
                            </p>
                            <p className="text-xs text-slate-500 uppercase">
                              {(invoice.currency || 'USD').toUpperCase()}
                            </p>
                            <div className="flex flex-col gap-2 mt-2 items-end">
                              {invoice.payment_code && (
                                <a
                                  href={`/invoice/${invoice.payment_code}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`invoice-view-${invoice.invoice_number}`}
                                  className="text-xs text-red-600 hover:underline"
                                >
                                  Ver factura
                                </a>
                              )}
                              {!isPaid && (
                                <Button
                                  data-testid={`mark-paid-btn-${invoice.invoice_number}`}
                                  onClick={() => handleMarkInvoicePaid(invoice.invoice_number)}
                                  disabled={isBusy}
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs py-1 px-3"
                                  size="sm"
                                >
                                  {isBusy ? (
                                    <>
                                      <RefreshCw size={14} className="mr-1 animate-spin" />
                                      Procesando...
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle size={14} className="mr-1" />
                                      Marcar como pagada
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payments List Tab */}
        {activeTab === 'payments' && (
          <Card className="border-0 shadow-2xl mb-6">
            <CardHeader className="bg-gradient-to-r from-slate-900 to-red-600 text-white">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-3">
                  <CreditCard size={24} />
                  Pagos de Stripe
                </CardTitle>
                <Button
                  onClick={fetchPayments}
                  disabled={loadingPayments}
                  className="bg-white/20 hover:bg-white/30 text-white"
                  size="sm"
                >
                  <RefreshCw size={16} className={`mr-2 ${loadingPayments ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {loadingPayments ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent mx-auto mb-4"></div>
                  <p className="text-slate-600">Cargando pagos...</p>
                </div>
              ) : payments.length === 0 ? (
                <div className="text-center py-8">
                  <CreditCard size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No hay pagos registrados</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {payments.map((payment, index) => (
                    <div 
                      key={payment.id || index}
                      className={`border rounded-lg p-4 ${
                        payment.payment_status === 'paid' 
                          ? 'border-green-200 bg-green-50' 
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {payment.payment_status === 'paid' ? (
                              <CheckCircle size={18} className="text-green-600" />
                            ) : (
                              <Clock size={18} className="text-yellow-600" />
                            )}
                            <span className={`font-semibold ${
                              payment.payment_status === 'paid' ? 'text-green-800' : 'text-slate-800'
                            }`}>
                              {payment.payment_status === 'paid' ? 'PAGADO' : 'PENDIENTE'}
                            </span>
                          </div>
                          <p className="font-medium text-slate-900">
                            {payment.package_name || 'Paquete'}
                          </p>
                          <p className="text-sm text-slate-600">
                            {payment.customer_email || 'Email no disponible'}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {formatDate(payment.createdAt)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-slate-900">
                            {payment.currency === 'eur' ? '€' : '$'}{payment.amount?.toFixed(2) || '0.00'}
                          </p>
                          <p className="text-xs text-slate-500 uppercase">
                            {payment.currency || 'USD'}
                          </p>
                          {payment.payment_status === 'paid' && (
                            <Button
                              onClick={() => generateInvoiceFromPayment(payment)}
                              className="mt-2 bg-green-600 hover:bg-green-700 text-white text-xs py-1 px-3"
                              size="sm"
                            >
                              <FileText size={14} className="mr-1" />
                              Generar Factura
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Orders Tab (Physical tool store) */}
        {activeTab === 'orders' && (
          <Card className="border-0 shadow-2xl mb-6" data-testid="orders-list-card">
            <CardHeader className="bg-gradient-to-r from-slate-900 to-red-600 text-white">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-3">
                  <Package size={24} />
                  Pedidos de la Tienda
                </CardTitle>
                <Button
                  data-testid="orders-refresh-btn"
                  onClick={fetchOrders}
                  disabled={loadingOrders}
                  className="bg-white/20 hover:bg-white/30 text-white"
                  size="sm"
                >
                  <RefreshCw size={16} className={`mr-2 ${loadingOrders ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {loadingOrders ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent mx-auto mb-4"></div>
                  <p className="text-slate-600">Cargando pedidos...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8">
                  <Package size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No hay pedidos registrados todavía</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => {
                    const sym = order.currency_symbol || (order.currency === 'eur' ? '€' : '$');
                    const isBusy = updatingOrderId === order.id;
                    const statusColors = {
                      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
                      paid: 'bg-blue-100 text-blue-800 border-blue-300',
                      shipped: 'bg-purple-100 text-purple-800 border-purple-300',
                      delivered: 'bg-green-100 text-green-800 border-green-300',
                      cancelled: 'bg-red-100 text-red-800 border-red-300',
                    };
                    const statusLabels = {
                      pending: 'Pendiente de pago',
                      paid: 'Pagado',
                      shipped: 'Enviado',
                      delivered: 'Entregado',
                      cancelled: 'Cancelado',
                    };
                    return (
                      <div
                        key={order.id}
                        data-testid={`order-row-${order.id}`}
                        className="border border-slate-200 rounded-lg p-4 bg-white"
                      >
                        <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                          <div className="flex-1 min-w-[220px]">
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${statusColors[order.status] || 'bg-slate-100 text-slate-700'}`}
                              >
                                {statusLabels[order.status] || order.status}
                              </span>
                              <span className="font-mono text-xs text-slate-400">
                                #{order.id?.slice(0, 8)}
                              </span>
                            </div>
                            <p className="font-medium text-slate-900">
                              {order.shipping_name || order.customer_name || order.customer_email}
                            </p>
                            <p className="text-sm text-slate-600">{order.customer_email}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              Creado: {formatDate(order.created_at)}
                              {order.paid_at && ` - Pagado: ${formatDate(order.paid_at)}`}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-slate-900">
                              {sym}{Number(order.subtotal || 0).toFixed(2)}
                            </p>
                            <p className="text-xs text-slate-500 uppercase">
                              {(order.currency || 'usd').toUpperCase()} - {order.items?.length || 0} items
                            </p>
                          </div>
                        </div>

                        <details className="bg-slate-50 rounded-md p-3 mb-3">
                          <summary className="cursor-pointer text-sm font-semibold text-slate-700">
                            Productos ({order.items?.length || 0})
                          </summary>
                          <ul className="text-xs text-slate-600 mt-2 space-y-1">
                            {(order.items || []).map((it, idx) => (
                              <li key={idx} className="flex justify-between">
                                <span>{it.name} x {it.quantity}</span>
                                <span className="font-mono">{sym}{Number(it.subtotal).toFixed(2)}</span>
                              </li>
                            ))}
                          </ul>
                        </details>

                        {order.fin_revenue !== undefined && (
                          <div className="bg-emerald-50 rounded-md p-3 mb-3 text-sm" data-testid={`order-margin-${order.id}`}>
                            <div className="flex items-center justify-between text-slate-600">
                              <span>Ingreso (venta)</span>
                              <span className="font-medium">{sym}{Number(order.fin_revenue || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between text-slate-500">
                              <span>Coste {order.fin_cost_known ? '' : '(parcial)'}</span>
                              <span>-{sym}{Number(order.fin_cost || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between text-slate-500">
                              <span>Comision Stripe</span>
                              <span>-{sym}{Number(order.fin_stripe_fee || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between font-semibold border-t border-emerald-200 mt-1 pt-1">
                              <span>Margen neto</span>
                              <span className={Number(order.fin_net_margin) >= 0 ? 'text-emerald-700' : 'text-red-600'}>
                                {sym}{Number(order.fin_net_margin || 0).toFixed(2)}
                              </span>
                            </div>
                            {!order.fin_cost_known && (
                              <p className="text-[11px] text-amber-600 mt-1">
                                Falta coste en algun producto (no mapeado a CJ/proveedor); margen aproximado.
                              </p>
                            )}
                          </div>
                        )}

                        {order.shipping_address && (
                          <details className="bg-blue-50 rounded-md p-3 mb-3">
                            <summary className="cursor-pointer text-sm font-semibold text-blue-900 flex items-center gap-2">
                              <Truck size={14} /> Direccion de envio
                            </summary>
                            <div className="text-xs text-slate-700 mt-2 leading-relaxed">
                              <p><strong>{order.shipping_name}</strong></p>
                              <p>{order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ''}</p>
                              <p>{order.shipping_address.postal_code} {order.shipping_address.city} {order.shipping_address.state}</p>
                              <p><strong>{order.shipping_address.country}</strong></p>
                              {order.shipping_phone && <p>Tel: {order.shipping_phone}</p>}
                            </div>
                          </details>
                        )}

                        <div className="flex flex-wrap gap-2 items-center justify-end pt-2 border-t border-slate-100">
                          {order.status === 'pending' && (
                            <Button
                              data-testid={`order-mark-paid-${order.id}`}
                              onClick={() => markOrderPaid(order.id)}
                              disabled={isBusy}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            >
                              <DollarSign size={14} className="mr-1" /> Marcar como Pagado + Enviar Email
                            </Button>
                          )}
                          {order.status === 'paid' && (
                            <>
                              <Button
                                data-testid={`order-resend-email-${order.id}`}
                                onClick={() => resendOrderEmail(order.id)}
                                disabled={isBusy}
                                size="sm"
                                variant="outline"
                                className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                              >
                                <Mail size={14} className="mr-1" /> Reenviar email + factura
                              </Button>
                              <Button
                                data-testid={`order-mark-shipped-${order.id}`}
                                onClick={() => updateOrder(order.id, { status: 'shipped' })}
                                disabled={isBusy}
                                size="sm"
                                className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                              >
                                <Truck size={14} className="mr-1" /> Marcar enviado
                              </Button>
                            </>
                          )}
                          {order.status === 'shipped' && (
                            <Button
                              data-testid={`order-mark-delivered-${order.id}`}
                              onClick={() => updateOrder(order.id, { status: 'delivered' })}
                              disabled={isBusy}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            >
                              <CheckCircle size={14} className="mr-1" /> Marcar entregado
                            </Button>
                          )}
                          {(order.status === 'pending' || order.status === 'paid') && (
                            <Button
                              data-testid={`order-cancel-${order.id}`}
                              onClick={() => {
                                if (window.confirm('Cancelar este pedido?')) {
                                  updateOrder(order.id, { status: 'cancelled' });
                                }
                              }}
                              disabled={isBusy}
                              size="sm"
                              variant="outline"
                              className="text-xs border-red-300 text-red-600 hover:bg-red-50"
                            >
                              Cancelar
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}


        {/* Create Invoice Tab */}
        {activeTab === 'create' && (
          <>
            {/* Success Message */}
            {generatedInvoice && (
              <Card className="border-0 shadow-2xl bg-green-50 border-2 border-green-500 mb-6">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle size={24} className="text-green-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-green-900 mb-2">
                        ¡Factura Creada Exitosamente!
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm text-green-800">Número de Factura:</Label>
                          <p className="font-mono text-green-900 font-semibold">
                            {generatedInvoice.invoice_number}
                          </p>
                    </div>
                    
                    <div>
                      <Label className="text-sm text-green-800">Código de Pago:</Label>
                      <p className="font-mono text-green-900 font-semibold">
                        {generatedInvoice.payment_code}
                      </p>
                    </div>

                    {/* Email Status */}
                    <div className={`rounded-lg p-3 ${generatedInvoice.email_sent ? 'bg-green-100' : 'bg-yellow-100'}`}>
                      <div className="flex items-center gap-2">
                        {generatedInvoice.email_sent ? (
                          <>
                            <Mail size={18} className="text-green-600" />
                            <span className="text-green-800 font-medium">
                              ✅ Email enviado automáticamente al cliente
                            </span>
                          </>
                        ) : (
                          <>
                            <AlertCircle size={18} className="text-yellow-600" />
                            <span className="text-yellow-800 font-medium">
                              ⚠️ Email no enviado - comparte el enlace manualmente
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm text-green-800">Enlace de Pago:</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={generatedInvoice.full_url}
                          readOnly
                          className="font-mono text-sm bg-white"
                        />
                        <Button
                          onClick={() => copyToClipboard(generatedInvoice.full_url)}
                          className="bg-green-600 hover:bg-green-700 flex-shrink-0"
                        >
                          <Copy size={16} className="mr-2" />
                          Copiar
                        </Button>
                      </div>
                    </div>

                    {!generatedInvoice.email_sent && (
                      <div className="bg-green-100 rounded-lg p-4 mt-4">
                        <p className="text-sm text-green-800 font-semibold mb-2">
                          📱 Envía este enlace al cliente por:
                        </p>
                        <ul className="text-sm text-green-700 space-y-1">
                          <li>• WhatsApp</li>
                          <li>• Email</li>
                          <li>• SMS</li>
                        </ul>
                      </div>
                    )}

                    <Button
                      onClick={resetForm}
                      className="w-full bg-slate-600 hover:bg-slate-700 mt-4"
                    >
                      Crear Nueva Factura
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

            {/* Form */}
            {!generatedInvoice && (
              <Card className="border-0 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-slate-900 to-red-600 text-white">
              <CardTitle className="flex items-center gap-3">
                <FileText size={24} />
                Crear Nueva Factura
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Información del Cliente */}
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <User size={20} className="text-red-600" />
                    Información del Cliente
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">
                        Nombre Completo *
                      </Label>
                      <Input
                        value={formData.client_name}
                        onChange={(e) => handleInputChange('client_name', e.target.value)}
                        placeholder="Juan Pérez"
                        required
                        className="border-slate-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">
                        Email *
                      </Label>
                      <Input
                        type="email"
                        value={formData.client_email}
                        onChange={(e) => handleInputChange('client_email', e.target.value)}
                        placeholder="cliente@email.com"
                        required
                        className="border-slate-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">
                        NIF / CIF / Tax ID
                      </Label>
                      <Input
                        value={formData.client_nif}
                        onChange={(e) => handleInputChange('client_nif', e.target.value)}
                        placeholder="12345678A"
                        className="border-slate-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">
                        Teléfono
                      </Label>
                      <Input
                        value={formData.client_phone}
                        onChange={(e) => handleInputChange('client_phone', e.target.value)}
                        placeholder="+1 (505) 226-0487"
                        className="border-slate-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">
                        Dirección
                      </Label>
                      <Input
                        value={formData.client_address}
                        onChange={(e) => handleInputChange('client_address', e.target.value)}
                        placeholder="123 Main St, Clovis NM"
                        className="border-slate-200"
                      />
                    </div>
                  </div>
                </div>

                {/* Información del Servicio */}
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <DollarSign size={20} className="text-red-600" />
                    Detalles del Servicio
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">
                        Nombre del Servicio *
                      </Label>
                      <Input
                        value={formData.service_name}
                        onChange={(e) => handleInputChange('service_name', e.target.value)}
                        placeholder="Instalación de Drywall"
                        required
                        className="border-slate-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">
                        Descripción del Servicio
                      </Label>
                      <Textarea
                        value={formData.service_description}
                        onChange={(e) => handleInputChange('service_description', e.target.value)}
                        placeholder="Descripción detallada del trabajo a realizar..."
                        rows={3}
                        className="border-slate-200"
                      />
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium">
                          Monto (sin impuestos) *
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.amount}
                          onChange={(e) => handleInputChange('amount', e.target.value)}
                          placeholder="500.00"
                          required
                          className="border-slate-200"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium">
                          Moneda
                        </Label>
                        <Select value={formData.currency} onValueChange={(value) => handleInputChange('currency', value)}>
                          <SelectTrigger className="border-slate-200">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD ($)</SelectItem>
                            <SelectItem value="EUR">EUR (€)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium">
                          Tasa de Impuesto (%)
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.tax_rate}
                          onChange={(e) => handleInputChange('tax_rate', e.target.value)}
                          placeholder="0.08"
                          className="border-slate-200"
                        />
                        <p className="text-xs text-slate-500">0.08 = 8%</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-700 font-medium">
                        Notas Adicionales
                      </Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        placeholder="Garantía, términos de pago, etc."
                        rows={2}
                        className="border-slate-200"
                      />
                    </div>
                  </div>
                </div>

                {/* Mark as Paid Option */}
                <div className="bg-green-50 rounded-lg p-4 flex items-center gap-3 border-2 border-green-200">
                  <input
                    type="checkbox"
                    id="mark_as_paid"
                    checked={formData.mark_as_paid}
                    onChange={(e) => handleInputChange('mark_as_paid', e.target.checked)}
                    className="w-5 h-5 text-green-600 border-slate-300 rounded focus:ring-green-500"
                  />
                  <label htmlFor="mark_as_paid" className="flex-1 cursor-pointer">
                    <span className="font-medium text-slate-900 flex items-center gap-2">
                      <CheckCircle size={18} className="text-green-600" />
                      Marcar como PAGADA (ya cobrada)
                    </span>
                    <p className="text-sm text-slate-600 mt-1">
                      Para pagos ya recibidos por Stripe, efectivo u otro método
                    </p>
                  </label>
                </div>

                {/* Email Option */}
                <div className="bg-blue-50 rounded-lg p-4 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="send_email"
                    checked={formData.send_email}
                    onChange={(e) => handleInputChange('send_email', e.target.checked)}
                    className="w-5 h-5 text-red-600 border-slate-300 rounded focus:ring-red-500"
                  />
                  <label htmlFor="send_email" className="flex-1 cursor-pointer">
                    <span className="font-medium text-slate-900 flex items-center gap-2">
                      <Mail size={18} className="text-blue-600" />
                      Enviar factura por email automáticamente
                    </span>
                    <p className="text-sm text-slate-600 mt-1">
                      {formData.mark_as_paid 
                        ? 'El cliente recibirá un email con la factura/recibo de pago'
                        : 'El cliente recibirá un email con el enlace de pago'}
                    </p>
                  </label>
                </div>

                {/* Botón Submit */}
                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 ${
                    formData.mark_as_paid 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                  size="lg"
                >
                  {loading ? (
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      Generando Factura...
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      {formData.mark_as_paid ? <CheckCircle size={24} /> : <FileText size={24} />}
                      {formData.mark_as_paid 
                        ? (formData.send_email ? 'Generar y Enviar Recibo de Pago' : 'Generar Recibo de Pago')
                        : (formData.send_email ? 'Generar y Enviar Factura' : 'Generar Factura')
                      }
                    </div>
                  )}
                </Button>

                <p className="text-sm text-slate-600 text-center">
                  * Campos requeridos
                </p>
              </form>
            </CardContent>
          </Card>
            )}

          {/* Info Card */}
          <Card className="border-0 shadow-lg bg-blue-50 mt-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">
                    ℹ️ Información Importante
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• El cliente recibirá un enlace para ver y pagar la factura</li>
                    <li>• Los pagos se procesan de forma segura con Stripe</li>
                    <li>• Recibirás notificación cuando el cliente pague</li>
                    <li>• El cliente puede pagar con tarjeta de crédito/débito</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
